import type React from 'react';
import { useEffect, useId } from 'react';
import { range } from 'lodash-es';

import {
  FieldSet,
  FormField,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Radio,
  Select,
} from '@zillow/constellation-10';

import type { Section } from '../../../models';
import type Field from '../../../models/Field';
import { BASE_RENT, LEASE_TERMS } from '../../../constants';
import { DATE_PICKER_FORMAT, MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import { format, isAfter, startOfToday } from 'date-fns';
import { getOrdinal } from '../../../utils/humanReadable';
import InlineTooltip from '../../Widgets/InlineTooltip';
import CurrencyInput from '../../Widgets/CurrencyInput';

const LAST_DAY_OF_MONTH = 32;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function BaseRent({ section, setSectionValues }: Props): JSX.Element {
  const radioGroupName = useId();
  const firstPayOnSignId = useId();
  const firstPayOnLeaseStartId = useId();

  useEffect(() => {
    section.setCustomFieldValidator(BASE_RENT.FIELD_NAME.AMOUNT, isAmountValid);
  }, []);

  function isAmountValid(amount: string, field?: Field): boolean {
    if (Number(amount.replace(/,/g, '')) > BASE_RENT.BOUNDS.MAX_AMOUNT) {
      (field ?? section.getField(BASE_RENT.FIELD_NAME.AMOUNT)).setErrorMessage(
        MAX_AMOUNT_ERROR_MESSAGE(BASE_RENT.BOUNDS.MAX_AMOUNT),
      );
      return false;
    }
    return true;
  }

  function onAmountChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.AMOUNT]: value,
    });
  }

  function onDueDateChange(event: React.ChangeEvent<HTMLSelectElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.DUE_DATE]: Number(value),
    });
  }

  function onPayDueClick(event: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({
      [BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN]: value === 'sign',
      [BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_START]: value === 'leaseStart',
    });
  }

  function startDayOfTheLeaseLabel(): string {
    const startDate = section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE);
    const formattedDate = startDate && format(startDate, DATE_PICKER_FORMAT);

    return `The lease start/move-in date${formattedDate ? ` (${formattedDate})` : ''}`;
  }

  const dueDay = section.getFieldValue(BASE_RENT.FIELD_NAME.DUE_DATE);

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs">
        Tell us about the rent
      </Heading>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.AMOUNT)}
        error={section.getField(BASE_RENT.FIELD_NAME.AMOUNT).hasError}
        label={
          <Label id="base-rent-amount">
            How much is{' '}
            <InlineTooltip
              tooltipBodyText="Base rent is the rent before other fees such as parking or utilities are added."
              closeWith={['blur']}
              openWith={['focus', 'click', 'mouseEnter']}
              shouldCloseOnOutsideClick
            >
              base rent
            </InlineTooltip>
            ?
          </Label>
        }
        control={
          <CurrencyInput
            aria-labelledby="base-rent-amount"
            value={section.getFieldValue(BASE_RENT.FIELD_NAME.AMOUNT)}
            onChange={onAmountChange}
            error={section.getField(BASE_RENT.FIELD_NAME.AMOUNT).hasError}
            endAdornment="per month"
          />
        }
        description={
          section.getField(BASE_RENT.FIELD_NAME.AMOUNT).hasError && (
            <FormHelp>{section.getField(BASE_RENT.FIELD_NAME.AMOUNT).getErrorMessage()}</FormHelp>
          )
        }
      />
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.DUE_DATE)}
        label={<Label id="monthly-due-date">What is the monthly due date?</Label>}
        control={
          <Select aria-labelledby="monthly-due-date" onChange={onDueDateChange} value={dueDay || ''}>
            {range(1, 29).map((day) => (
              <option key={day} value={day}>
                {dueDay === day ? `${getOrdinal(day)} day of the month` : day}
              </option>
            ))}
            <option value={LAST_DAY_OF_MONTH}>Last day of the month</option>
          </Select>
        }
      />
      <FieldSet
        required
        disabled={section.isFieldDisabled(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
        legend={<Legend>When is the first monthly rent payment due?</Legend>}
        error={
          section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE) &&
          isAfter(startOfToday(), section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE))
        }
        description={
          section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE) &&
          isAfter(startOfToday(), section.getFieldValue(LEASE_TERMS.FIELD_NAME.START_DATE)) ? (
            <FormHelp>
              Start date cannot be in the past, first change it on the <strong>Lease terms</strong> screen
            </FormHelp>
          ) : (
            <FormHelp>Monthly rent includes the base rent and other fixed monthly fees, such as parking</FormHelp>
          )
        }
      >
        <LabeledControl
          controlId={firstPayOnSignId}
          label={<Label>Upon signing the lease</Label>}
          control={
            <Radio
              id={firstPayOnSignId}
              name={radioGroupName}
              value="sign"
              checked={section.getFieldValue(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_SIGN)}
              onChange={onPayDueClick}
            />
          }
        />
        <LabeledControl
          controlId={firstPayOnLeaseStartId}
          label={<Label>{startDayOfTheLeaseLabel()}</Label>}
          control={
            <Radio
              id={firstPayOnLeaseStartId}
              name={radioGroupName}
              value="leaseStart"
              checked={section.getFieldValue(BASE_RENT.FIELD_NAME.FIRST_PAY_DUE_START)}
              onChange={onPayDueClick}
            />
          }
        />
      </FieldSet>
    </>
  );
}
